import { EXPLORER_CURRENCY } from '@/lib/fxToUsd';
import { Check, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LComponents } from '@/components/i18n';
import { useLocale } from '@/contexts/LocaleContext';
import { useLocalizedLocations } from '@/hooks/useLocalizedLocations';
import { cn } from '@/lib/utils';
import type { TaxResult } from '@/types/taxExplorer';

interface BestForYouListProps {
  results: TaxResult[];
  hasResidence: boolean;
  pinned: number[];
  selected: number | null;
  onSelect: (locationId: number) => void;
  onPin: (locationId: number) => void;
}

function flag(code: string): string {
  if (code.length !== 2) return '🌐';
  return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
}

export function BestForYouList({ results, hasResidence, pinned, selected, onSelect, onPin }: BestForYouListProps): JSX.Element {
  const { formatCompactCurrency, formatNumber } = useLocale();
  const { locations: localized } = useLocalizedLocations();
  const nameFor = (r: TaxResult): string =>
    localized.find((l) => (l.iso ?? '').toUpperCase() === r.countryCode.toUpperCase())?.name || r.countryName;

  return (
    <section aria-labelledby="best-for-you" className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 id="best-for-you" className="text-base font-semibold text-foreground">
          <LComponents id="tax_explorer.best.title" defaultValue="Best for you" />
        </h2>
        <span className="text-xs text-muted-foreground">
          {hasResidence
            ? <LComponents id="tax_explorer.best.hint_compare" defaultValue="Lowest tax for your situation, compared with where you live" />
            : <LComponents id="tax_explorer.best.hint_rate" defaultValue="Lowest tax for your situation — choose where you live to see savings" />}
        </span>
      </div>
      <ol className="flex flex-col divide-y divide-border">
        {results.map((r, i) => {
          const isPinned = pinned.includes(r.locationId);
          return (
            <li key={r.locationId} className={cn('flex items-center gap-3 py-2', selected === r.locationId && 'bg-muted/50')}>
              <span className="w-5 text-sm text-muted-foreground">{i + 1}</span>
              <button type="button" onClick={() => onSelect(r.locationId)} className="flex flex-1 items-center gap-2 text-start">
                <span aria-hidden="true">{flag(r.countryCode)}</span>
                <span className="font-medium text-foreground">{nameFor(r)}</span>
              </button>
              <span className="w-14 text-end text-sm text-foreground">{formatNumber(r.effectiveRate, { maximumFractionDigits: 1 })}%</span>
              <span className={cn('hidden w-32 text-end text-sm sm:inline', r.savingsVsCurrent > 0 ? 'text-primary' : 'text-muted-foreground')}>
                {hasResidence
                  ? (r.savingsVsCurrent > 0
                    ? <><LComponents id="tax_explorer.best.saves" defaultValue="saves" /> {formatCompactCurrency(r.savingsVsCurrent, EXPLORER_CURRENCY)}</>
                    : <LComponents id="tax_explorer.best.no_savings" defaultValue="no savings" />)
                  : formatCompactCurrency(r.totalTax, EXPLORER_CURRENCY)}
              </span>
              <Button type="button" size="sm" variant={isPinned ? 'secondary' : 'outline'} onClick={() => onPin(r.locationId)}
                disabled={!isPinned && pinned.length >= 5}>
                {isPinned ? <Check className="h-3 w-3 me-1" aria-hidden="true" /> : <Pin className="h-3 w-3 me-1" aria-hidden="true" />}
                <LComponents id="tax_explorer.best.compare" defaultValue="Compare" />
              </Button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
